import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable } from 'rxjs';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { find } from 'lodash';
import { BasicItem } from '../../types/basic-item';

/**
 * @deprecated please use <ml-chips-autocomplete> instead
 */
@Component({
  selector: 'ml-chip-input',
  template: `
    <mat-form-field class="full-width">
      <mat-label>{{ label | translate | titlecase }}</mat-label>
      <mat-chip-list #chipList>
        <mat-chip
          *ngFor="let value of valuesControl.value"
          [selectable]="true"
          [removable]="true"
          (removed)="remove(value)"
        >
          {{ displayFn(value) }}
          <mat-icon matChipRemove svgIcon="cancel"></mat-icon>
        </mat-chip>
        <input
          placeholder="{{ placeholder | translate | titlecase }}"
          #valueInput
          [formControl]="valueControl"
          [matAutocomplete]="auto"
          [matChipInputFor]="chipList"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          [matChipInputAddOnBlur]="true"
          (matChipInputTokenEnd)="add($event)"
        />
      </mat-chip-list>
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="selected($event)"
      >
        <mat-option
          *ngFor="let filteredValue of (filteredValues | async)"
          [value]="filteredValue"
        >
          {{ displayFn(filteredValue) }}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
  `,
  styleUrls: ['./chip-input.component.scss']
})
export class ChipInputComponent implements OnInit {
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredValues: Observable<BasicItem[]>;
  allValues: BasicItem[];
  valueControl = new FormControl();

  @ViewChild('valueInput', { static: true }) permissionInput: ElementRef<
    HTMLInputElement
  >;
  @ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;

  @Input() label: string;
  @Input() placeholder: string;
  @Input() valuesControl: FormControl;
  @Input() displayFn = item => item.name;

  @Input() set values(values: any[]) {
    if (values) {
      if (this.valuesControl) {
        this.allValues = values.filter(
          value =>
            !find(
              this.valuesControl.value,
              (_value: BasicItem) => _value.id === value.id
            )
        );
      } else {
        this.allValues = [...values];
      }
    } else {
      this.allValues = [];
    }
    this.initFilter();
  }

  constructor() {}

  private initFilter() {
    this.filteredValues = this.valueControl.valueChanges.pipe(
      startWith(null),
      map((value: string | null) =>
        value ? this._filter(value) : this.allValues.slice()
      )
    );
  }

  ngOnInit() {}

  selected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    this.valuesControl.setValue([...this.valuesControl.value, value]);
    const valueIndex = this.allValues.findIndex(_autocompleteValue => {
      return _autocompleteValue === value;
    });
    this.allValues.splice(valueIndex, 1);
    this.permissionInput.nativeElement.value = '';
    this.valueControl.setValue(null);
  }

  add(event: MatChipInputEvent): void {
    if (!this.matAutocomplete.isOpen) {
      const input = event.input;
      const value = event.value;

      if ((value || '').trim()) {
        const valueIndex = this.allValues.findIndex(
          (_item: BasicItem) => _item.name === value.trim()
        );

        if (valueIndex !== -1) {
          this.valuesControl.setValue([
            ...this.valuesControl.value,
            this.allValues[valueIndex]
          ]);
          this.allValues.splice(valueIndex, 1);
        }
      }

      if (input) {
        input.value = '';
      }

      this.valueControl.setValue(null);
    }
  }

  remove(value: BasicItem): void {
    const index = this.valuesControl.value.indexOf(value);
    if (index >= 0) {
      this.allValues.push(value);
      this.valuesControl.value.splice(index, 1);
      this.valuesControl.setValue([...this.valuesControl.value]);
    }
  }

  private _filter(value: string) {
    if (typeof value !== 'string') {
      return;
    }
    const filterValue = value.toLowerCase();

    return this.allValues.filter(
      (_value: BasicItem) =>
        this.displayFn(_value)
          .toLowerCase()
          .indexOf(filterValue) >= 0
    );
  }
}
